import { eosErrorCode, eosErrorMessage } from '@/lib/eos/errors';

/**
 * Maps the training routes' `{ error: code }` envelopes to copy the UI
 * can toast or render inline. Reuses the generic envelope readers from
 * `lib/eos/errors` — the server envelope shape is identical.
 */
export function describeTrainingError(err: unknown, fallback: string): string {
  const code = eosErrorCode(err);
  switch (code) {
    case 'forbidden':
      return eosErrorMessage(err) ?? "You don't have permission for that.";
    case 'validation_failed':
      return 'Some fields look invalid — double-check and retry.';
    case 'not_enrolled':
      return 'You are not enrolled in this course.';
    case 'course_not_found':
      return 'That course no longer exists — refresh the page.';
    case 'lesson_not_found':
      return 'That lesson no longer exists — refresh the page.';
    case 'quiz_not_found':
      return 'That quiz no longer exists — refresh the page.';
    case 'attempts_exhausted':
      return eosErrorMessage(err) ?? 'You have used all your attempts for this quiz.';
    case 'course_empty':
      return eosErrorMessage(err) ?? 'Add at least one lesson before publishing.';
    case 'course_published':
      return eosErrorMessage(err) ?? 'Lessons can only be deleted while the course is a draft.';
    case 'quiz_needs_questions':
      return 'A quiz needs at least one question.';
    case 'question_needs_options':
      return 'Every question needs at least two options.';
    case 'duplicate_option_ids':
      return 'Option ids must be unique within a question.';
    case 'invalid_correct_option':
      return 'Mark one of the listed options as the correct answer.';
    case 'invalid_lesson_order':
      return 'The lesson list changed while reordering — refresh and try again.';
    case 'sop_not_found':
      return 'That SOP no longer exists.';
    case 'sop_not_approved':
      return eosErrorMessage(err) ?? 'The SOP has no approved revision yet.';
    case 'no_approved_version':
      return eosErrorMessage(err) ?? 'The SOP has no approved revision to acknowledge.';
    case 'certificate_not_found':
      return 'No certificate matches that code in this organization.';
    case 'assignment_not_found':
      return 'That assignment no longer exists — refresh the page.';
    case 'assignment_inactive':
      return 'The assignment is deactivated — reactivate it first.';
    case 'target_membership_not_found':
      return 'That member is not part of this organization.';
    case 'target_department_not_found':
      return 'That department no longer exists.';
    case 'template_not_found':
      return 'That checklist template no longer exists — refresh the page.';
    case 'template_or_employee_not_found':
      return 'The template or employee no longer exists — refresh the page.';
    case 'instance_not_found':
      return 'That checklist no longer exists — refresh the page.';
    case 'item_not_found':
      return 'That checklist item no longer exists — refresh the page.';
    case 'preview_mode':
      return eosErrorMessage(err) ?? 'Training records cannot be created while previewing as another role.';
    default:
      return eosErrorMessage(err) ?? (err instanceof Error ? err.message : fallback);
  }
}
